<template>
  <v-card v-bind:class="actionClass">
    <!-- Input meta info & toolbar -->
    <v-toolbar flat color="#f5f5ff">
      <v-toolbar-title class="blue-grey--text">
        <span class="subtitle-1">{{ $t('input') }}</span>
        <span class="caption pl-2">LVL {{ level }}</span>
        <span class="caption pl-2">IDX {{ index }}</span>
        <span class="caption pl-2">{{ typeLabel }}</span>
      </v-toolbar-title>
      <v-spacer class="handle"></v-spacer>
      <v-btn class="caption" v-bind:disabled="sourceDisabled" @click="addSource" v-if="toolbarEnabled && showAddSource">
        <v-icon>add</v-icon>
        {{ $t('source') }}
      </v-btn>
      <v-menu open-on-hover offset-y v-if="toolbarEnabled">
        <template v-slot:activator="{ on }">
          <v-btn text icon class="caption" v-on="on">
            <v-icon>more_vert</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-btn text icon :title="copyTitle" :color="inCopy ? 'grey' : 'black'" @click="copy">
            <v-icon>file_copy</v-icon>
          </v-btn>
          <v-btn text icon :title="cutTitle" :color="inCut ? 'grey' : 'black'" @click="cut">
            <v-icon>flip_to_back</v-icon>
          </v-btn>
          <v-btn text icon color="error" :title="$t('remove')" @click="remove">
            <v-icon>delete_forever</v-icon>
          </v-btn>
        </v-list>
      </v-menu>
    </v-toolbar>

    <!-- Input fields editor -->
    <v-container fluid class="pa-2">
      <v-layout>
        <v-flex>
          <v-text-field v-model="localLabel" :label="$t('label')"></v-text-field>
        </v-flex>
        <v-flex v-if="selectedLanguage" class="ml-3">
          <v-text-field v-model="localTranslatedLabel" :label="translatedLabel"></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex>
          <v-text-field v-model="localDescription" :label="$t('description')"></v-text-field>
        </v-flex>
        <v-flex v-if="selectedLanguage" class="ml-3">
          <v-text-field v-model="localTranslatedDescription" :label="translatedDescription"></v-text-field>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex shrink v-if="sourceWarning">
          <v-icon color="orange" class="ma-3" :title="$t('selected-source-type-not-matching-device-type')">warning
          </v-icon>
        </v-flex>
        <v-flex xs10 sm6 md4 v-if="displaySource">
          <v-autocomplete v-model="localSource"
                          :items="sourceVariables"
                          :label="$t('source')"
                          item-value="name"
                          item-text="name"
                          clearable
          ></v-autocomplete>
        </v-flex>
        <v-flex xs6 sm6 md4 v-if="displaySource && source && source.type">
          <v-select class="ml-3"
            v-model="localSourceUnit"
            :label="$t('source-type')"
            :items="localSourceUnits"
            item-value="value"
            item-text="label"
          ></v-select>
        </v-flex>
        <v-flex xs6 sm6 md2 v-if="displaySource && localSourceUnit">
          <v-select class="ml-3"
            v-model="localSourceDisplayUnit"
            :label="$t('display-unit')"
            :items="localSourceDisplayUnits"
          ></v-select>
        </v-flex>
      </v-layout>
    </v-container>
  </v-card>
</template>

<script>
import editorMixins from './editor-mixins.es6'

export default {
  name: 'graph-editor',
  mixins: [editorMixins],
  props: {
    type: String,
    level: Number,
    index: Number,
    label: String,
    description: String,
    translation: Object,
    source: Object
  },
  data () {
    return {

    }
  },
  computed: {
    // source: display "add source" button
    showAddSource () {
      const hasType = this.$store.getters['checklist/deviceType'] !== null
      const hasVars = this.sourceVariables.length !== 0

      return hasType && hasVars
    },
    // source: disable "add source" if source is set
    sourceDisabled () {
      return this.source && this.source.name !== null
    },
    // source: display "source select"
    displaySource () {
      return this.showAddSource && this.sourceDisabled
    },
    sourceWarning () {
      const type = this.$store.getters['checklist/deviceType']
      return this.displaySource && this.source.type && this.source.type !== type
    },
    localSourceUnit: {
      get () {
        return this.source ? this.source.unit : null
      },
      set (value) {
        const unit = value || null
        const displayUnit = unit ? this.$store.getters['checklist/displayUnits'][unit][0] : null
        const receiveUnit = displayUnit ? this.$store.getters['checklist/receiveUnit'](unit) : null

        const source = {
          ...this.source,
          unit,
          displayUnit,
          receiveUnit
        }
        this.$nextTick(() => {
          this.eventBus.$emit('update', {
            idx: this.index,
            source
          })
        })
      }
    },
    localSourceUnits () {
      return this.$store.getters['checklist/units'].map(unit => ({
        value: unit.value,
        label: this.translateLabel(unit.label)
      }))
    },
    localSourceDisplayUnit: {
      get () {
        return this.source ? this.source.displayUnit : null
      },
      set (value) {
        const displayUnit = value || null
        const source = {
          ...this.source,
          displayUnit
        }
        this.$nextTick(() => {
          this.eventBus.$emit('update', {
            idx: this.index,
            source
          })
        })
      }
    },
    localSourceDisplayUnits () {
      const displayUnits = this.$store.getters['checklist/displayUnits']
      if (this.localSourceUnit) {
        return displayUnits[this.localSourceUnit]
      } else {
        return [null]
      }
    },
    sourceVariables () {
      const type = this.$store.getters['checklist/deviceType']
      const vars = []
      const deviceType = this.$store.state.deviceTypes
        .filter(({ name }) => name === type).shift()
      if (type && deviceType) {
        const variables = Object.keys(deviceType.checklistVariables)
          .filter(name => (/curve/i).test(name))
        for (let name of variables) {
          vars.push({
            name,
            type
          })
        }
      }
      return vars
    },
    typeLabel () {
      return this.type.toUpperCase()
    }
  },
  methods: {
    addSource () {
      this.eventBus.$emit('update', {
        idx: this.index,
        source: {
          name: '',
          type: '',
          unit: null,
          displayUnit: null
        }
      })
    },
    translateLabel (label) {
      const prefix = 'units'
      const result = this.$t([prefix, label.toLowerCase()].join('-'))
      return result.indexOf(prefix) === 0
        ? label
        : result
    }
  },
  filters: {
    friendlyLabel (val) {
      return val.replace(/([A-Z])/g, ' $1').toLowerCase()
    }
  }
}
</script>
